/**
 * Role helper utilities — handles both old (role string) and new (roles array)
 * user document formats for backwards compatibility.
 *
 * Always use these helpers instead of inline role checks throughout the app.
 */

// The internal team. Canonical list — mirrors isInternalTeam() in
// firestore.rules and isInternalUser() in storage.rules. Keep the three in step.
export const INTERNAL_ROLES = ['analyst', 'head_of_pipeline', 'cio', 'md', 'admin'];

// Roles permitted into the internal portal shell (/dashboard, /pipeline, the
// review routes). IC members are not on the internal team, but the rules grant
// them deal access (firestore.rules: deals read via isICMemberForFund, plus
// ic_submissions and ic_votes), so the UI must not be stricter than the
// database or a legitimate role loses pages it can read today.
export const PORTAL_ROLES = [...INTERNAL_ROLES, 'ic_member'];

export const getUserRoles = (userDoc) => {
  if (!userDoc) return [];
  if (Array.isArray(userDoc.roles)) return userDoc.roles;
  if (userDoc.role) return [userDoc.role];
  return [];
};

export const hasRole = (userDoc, role) =>
  getUserRoles(userDoc).includes(role);

export const hasAnyRole = (userDoc, roles) => {
  const userRoles = getUserRoles(userDoc);
  return roles.some(r => userRoles.includes(r));
};

export const isAdmin = (userDoc) => hasRole(userDoc, 'admin');
export const isAnalyst = (userDoc) => hasRole(userDoc, 'analyst');
export const isHeadOfPipeline = (userDoc) => hasRole(userDoc, 'head_of_pipeline');
export const isCIO = (userDoc) => hasRole(userDoc, 'cio');
export const isMD = (userDoc) => hasRole(userDoc, 'md');
export const isICMember = (userDoc) => hasRole(userDoc, 'ic_member');
export const isPrincipal = (userDoc) =>
  hasAnyRole(userDoc, ['head_of_pipeline', 'cio', 'md']);
export const isInternalTeam = (userDoc) => hasAnyRole(userDoc, INTERNAL_ROLES);

// True when the user may enter the internal portal shell at all. Companies and
// users whose roles have not resolved are excluded.
export const hasPortalAccess = (userDoc) => hasAnyRole(userDoc, PORTAL_ROLES);
